/**
 * wordWriteQueue — the process-wide single-writer queue for Word.run writes.
 *
 * ``runWord`` (wordWriter.ts) is a bare ``Word.run``: nothing in Office.js
 * serialises two concurrent consumers, and two overlapping batches corrupt each
 * other's paragraph indices — the failure mode behind the "blanks a few seconds
 * after Load" class of bugs.
 *
 * This used to live inside ``useWordWriteQueue`` as a hook-local ref, which
 * served the SSE write path but left every write outside that hook unserialised.
 * ``reviewStore``'s audit re-projection is the one that actually collides: the
 * backend auto-audits each section CONCURRENTLY with the next module drafting,
 * so once live projection is on, an ``audit_complete`` re-projection and a
 * ``section_ready`` write genuinely race for the Office.js context.
 *
 * Module-level (not per-hook) is the point: a remount must not hand out a second
 * independent queue.
 */

/** Tail of the single promise chain every Word write appends to. */
let tail: Promise<void> = Promise.resolve();

/** Writes queued or running. */
let pending = 0;

/** One-shot callbacks waiting for the queue to drain. */
const idleWaiters = new Set<() => void>();

/** What the queue is doing, for the pane's "updating the document" strip. */
export interface WordWriteActivity {
	busy: boolean;
	/** Label of the write running now (``null`` when it has none). */
	label: string | null;
}

let runningLabel: string | null = null;
const activityListeners = new Set<(activity: WordWriteActivity) => void>();

function notifyActivity(): void {
	const activity: WordWriteActivity = { busy: pending > 0, label: pending > 0 ? runningLabel : null };
	for (const l of Array.from(activityListeners)) {
		try {
			l(activity);
		} catch (err) {
			console.warn("[wordWriteQueue] activity listener threw (non-fatal):", err);
		}
	}
}

/** Subscribe to queue busy/label changes; fires now with the current value.
 *  Returns the unsubscribe function. */
export function subscribeWordWriteActivity(cb: (activity: WordWriteActivity) => void): () => void {
	activityListeners.add(cb);
	cb({ busy: pending > 0, label: pending > 0 ? runningLabel : null });
	return () => {
		activityListeners.delete(cb);
	};
}

/**
 * Chain a Word-write closure onto the single-writer queue.
 *
 * Rejections are absorbed on the queue tail so a failing write can't stall
 * every subsequent one, while the returned promise still reflects the real
 * outcome so callers can surface their own error UI.
 *
 * ``label`` (optional) says what the write does — shown to the writer while it
 * runs ("Applying your edit").
 */
export function enqueueWordWrite<T>(fn: () => Promise<T>, label?: string): Promise<T> {
	pending += 1;
	if (pending === 1) notifyActivity();
	const next = tail.then(() => {
		runningLabel = label ?? null;
		notifyActivity();
		return fn();
	});
	tail = next.then(
		() => undefined,
		() => undefined,
	);
	void tail.then(() => {
		pending -= 1;
		runningLabel = null;
		notifyActivity();
		if (pending === 0 && idleWaiters.size > 0) {
			const waiters = Array.from(idleWaiters);
			idleWaiters.clear();
			for (const w of waiters) {
				try {
					w();
				} catch (err) {
					console.warn("[wordWriteQueue] idle waiter threw (non-fatal):", err);
				}
			}
		}
	});
	return next;
}

/**
 * True while any queued Word write is pending or running. Background read
 * probes (polls) check this and skip their tick: on Word desktop every
 * Word.run is serviced on Word's UI thread, so a probe queued behind a long
 * write only adds to the stall and then fires late.
 */
export function isWordWriteQueueBusy(): boolean {
	return pending > 0;
}

/**
 * Run ``cb`` once the queue is idle — now if it already is, otherwise when the
 * last pending write settles. Selection / paragraph events raised by our own
 * writes are parked here and replayed once, instead of each one starting a
 * Word read behind the write. Returns a cancel function.
 */
export function whenWordWriteQueueIdle(cb: () => void): () => void {
	if (pending === 0) {
		cb();
		return () => {};
	}
	idleWaiters.add(cb);
	return () => {
		idleWaiters.delete(cb);
	};
}

/** Test hook — drop any pending chain so cases can't leak into each other. */
export function __resetWordWriteQueueForTests(): void {
	tail = Promise.resolve();
	pending = 0;
	runningLabel = null;
	idleWaiters.clear();
	activityListeners.clear();
}
